import { FC, MouseEvent } from 'react';
import { ClickAwayListener } from '@mui/material';
import { useToggle } from '@csp/csp-web-hooks';
import { usePointerSupport } from '../../../hooks/usePointerSupport';
import { Box } from '../../layout/box/Box';
import { Tooltip } from './Tooltip';
import { ClickableTooltipProps } from './model/ClickableTooltipProps';

/**
 * The clickable tooltip can be used to support tooltips on devices where the user cannot hover an item to open the tooltip.
 *
 * The component checks if a moise is connected if not - the tooltip will be shown when the tooltip's children is clicked
 *
 * @param title - the content to be shown in the tooltip
 * @param onlyClickable - over will be deactivated and the children but be clicked to show the tooltip information
 * @param arrow - arrow is shown by default. Can be overridden
 */
export const ClickableTooltip: FC<ClickableTooltipProps> = ({
  onlyClickable,
  arrow = true,
  placement = 'top',
  children,
  boxProps,
  ...props
}) => {
  const { hasMouse } = usePointerSupport();
  const toggle = useToggle();

  const handleClick = (event: MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    event.preventDefault();
    toggle.toggle();
  };

  return !hasMouse || onlyClickable ? (
    <ClickAwayListener onClickAway={toggle.setOff}>
      <Box display="inline" {...boxProps}>
        <Tooltip
          disableFocusListener
          disableHoverListener
          disableTouchListener
          arrow={arrow}
          open={toggle.isOn}
          onClose={toggle.setOff}
          onClick={handleClick}
          style={{ cursor: 'pointer' }}
          {...props}
        >
          {children}
        </Tooltip>
      </Box>
    </ClickAwayListener>
  ) : (
    <Tooltip placement={placement} arrow={arrow} {...props}>
      {children}
    </Tooltip>
  );
};
